import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useVendor } from '../../contexts/VendorContext';
import VendorLayout from '../../components/VendorLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Settings, User, Building, Phone, Mail, MapPin, 
  CreditCard, Save, Loader2, CheckCircle, AlertCircle,
  Shield, Key, Bell, Truck, Package, Store
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

export default function VendorSettings() {
  const { vendor, token, updateVendor } = useVendor();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const navigate = useNavigate();
  
  const [profileData, setProfileData] = useState({
    business_name: '',
    owner_name: '',
    email: '',
    phone: '',
    description: ''
  });

  const [addressData, setAddressData] = useState({
    street: '',
    city: '',
    state: '',
    pincode: ''
  });

  const [pickupData, setPickupData] = useState({
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    pincode: '',
    phone: '',
    email: ''
  });

  const [bankData, setBankData] = useState({
    bank_name: '',
    account_holder: '',
    bank_account: '',
    bank_ifsc: '',
    gst_number: '',
    pan_number: ''
  });
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (!vendor) return;
    
    setProfileData({
      business_name: vendor.business_name || '',
      owner_name: vendor.owner_name || '',
      email: vendor.email || '',
      phone: vendor.phone || '',
      description: vendor.description || ''
    });
    
    setAddressData({
      street: vendor.address?.street || '',
      city: vendor.address?.city || '',
      state: vendor.address?.state || '',
      pincode: vendor.address?.pincode || ''
    });

    // Pre-fill pickup location
    if (vendor.pickup_location) {
      setPickupData({
        address_line1: vendor.pickup_location.address_line1 || '',
        address_line2: vendor.pickup_location.address_line2 || '',
        city: vendor.pickup_location.city || '',
        state: vendor.pickup_location.state || '',
        pincode: vendor.pickup_location.pincode || '',
        phone: vendor.pickup_location.phone || vendor.phone || '',
        email: vendor.pickup_location.email || vendor.email || ''
      });
    } else {
      // Use business address as default
      setPickupData({
        address_line1: vendor.address?.street || '',
        address_line2: '',
        city: vendor.address?.city || '',
        state: vendor.address?.state || '',
        pincode: vendor.address?.pincode || '',
        phone: vendor.phone || '',
        email: vendor.email || ''
      });
    }
    
    setBankData({
      bank_name: vendor.kyc?.bank_name || '',
      account_holder: vendor.kyc?.account_holder || '',
      bank_account: vendor.kyc?.bank_account || '',
      bank_ifsc: vendor.kyc?.bank_ifsc || '',
      gst_number: vendor.kyc?.gst_number || '',
      pan_number: vendor.kyc?.pan_number || ''
    });
  }, [vendor]);

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const response = await axios.put(
        `${API_URL}/api/vendor/profile`,
        {
          business_name: profileData.business_name,
          owner_name: profileData.owner_name,
          email: profileData.email,
          description: profileData.description,
          address: addressData
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (updateVendor) {
        updateVendor(response.data);
      }
      toast.success('Profile updated successfully!');
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePickupLocation = async () => {
    if (!pickupData.address_line1 || !pickupData.city || !pickupData.state || !pickupData.pincode) {
      toast.error('Please fill all required pickup location fields');
      return;
    }

    setSaving(true);
    try {
      const response = await axios.put(
        `${API_URL}/api/vendor/pickup-location`,
        pickupData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (updateVendor) {
        updateVendor(response.data);
      }
      toast.success('Pickup location updated successfully!');
    } catch (error) {
      console.error('Error updating pickup location:', error);
      toast.error(error.response?.data?.detail || 'Failed to update pickup location');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveBank = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/vendor/kyc`,
        {
          bank_name: bankData.bank_name,
          account_holder: bankData.account_holder,
          bank_account: bankData.bank_account,
          bank_ifsc: bankData.bank_ifsc,
          gst_number: bankData.gst_number,
          pan_number: bankData.pan_number
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Bank details updated successfully!');
    } catch (error) {
      console.error('Error updating bank details:', error);
      toast.error(error.response?.data?.detail || 'Failed to update bank details');
    } finally {
      setSaving(false);
    }
  };

  const copyFromBusinessAddress = () => {
    setPickupData({
      ...pickupData,
      address_line1: addressData.street,
      city: addressData.city,
      state: addressData.state,
      pincode: addressData.pincode,
      phone: profileData.phone,
      email: profileData.email
    });
    toast.success('Copied from business address');
  };

  const tabs = [
    { id: 'profile', label: 'Business Profile', icon: Building },
    { id: 'pickup', label: 'Pickup Location', icon: Truck },
    { id: 'bank', label: 'Bank & KYC', icon: CreditCard },
    { id: 'notifications', label: 'Notifications', icon: Bell }
  ];

  return (
    <VendorLayout>
      <div className="space-y-6" data-testid="vendor-settings-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Settings className="w-7 h-7 text-gray-600" />
            Settings
          </h1>
          <p className="text-gray-500">Manage your store profile, pickup location, and bank details</p>
        </div>

        {/* Status Card */}
        <Card className={vendor?.status === 'approved' ? 'border-green-200 bg-green-50' : 'border-yellow-200 bg-yellow-50'}>
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {vendor?.status === 'approved' ? (
                <CheckCircle className="w-6 h-6 text-green-600" />
              ) : (
                <AlertCircle className="w-6 h-6 text-yellow-600" />
              )}
              <div>
                <p className="font-medium text-gray-900">Account Status</p>
                <p className="text-sm text-gray-600">
                  {vendor?.status === 'approved' 
                    ? 'Your seller account is active and verified'
                    : 'Your account is pending approval'}
                </p>
              </div>
            </div>
            <Badge variant={vendor?.status === 'approved' ? 'default' : 'secondary'} className={vendor?.status === 'approved' ? 'bg-green-600' : ''}>
              {vendor?.status?.charAt(0).toUpperCase() + vendor?.status?.slice(1)}
            </Badge>
          </CardContent>
        </Card>

        {/* Tabs */}
        <div className="flex gap-2 border-b overflow-x-auto">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-green-600 text-green-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Store className="w-5 h-5" />
                  Business Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="business_name">Business Name</Label>
                  <Input
                    id="business_name"
                    value={profileData.business_name}
                    onChange={(e) => setProfileData({ ...profileData, business_name: e.target.value })}
                    placeholder="Your store name"
                  />
                </div>
                <div>
                  <Label htmlFor="owner_name">Owner Name</Label>
                  <Input
                    id="owner_name"
                    value={profileData.owner_name}
                    onChange={(e) => setProfileData({ ...profileData, owner_name: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="description">Store Description</Label>
                  <Textarea
                    id="description"
                    value={profileData.description}
                    onChange={(e) => setProfileData({ ...profileData, description: e.target.value })}
                    placeholder="Describe your products and specialties..."
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  Business Address
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="street">Street Address</Label>
                  <Input
                    id="street"
                    value={addressData.street}
                    onChange={(e) => setAddressData({ ...addressData, street: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="city">City</Label>
                    <Input
                      id="city"
                      value={addressData.city}
                      onChange={(e) => setAddressData({ ...addressData, city: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="state">State</Label>
                    <Input
                      id="state"
                      value={addressData.state}
                      onChange={(e) => setAddressData({ ...addressData, state: e.target.value })}
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="pincode">Pincode</Label>
                  <Input
                    id="pincode"
                    value={addressData.pincode}
                    onChange={(e) => setAddressData({ ...addressData, pincode: e.target.value })}
                  />
                </div>
                <Button onClick={handleSaveProfile} disabled={saving} className="w-full bg-green-600 hover:bg-green-700">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Profile
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Pickup Location Tab */}
        {activeTab === 'pickup' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Truck className="w-5 h-5 text-green-600" />
                Pickup Location for Dispatch
              </CardTitle>
              <CardDescription>
                This address will be used by Shiprocket to pick up your products for delivery. 
                Shipping costs are calculated from this location to the customer.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-2 text-blue-700">
                  <Package className="w-5 h-5" />
                  <span className="text-sm font-medium">Use same as business address?</span>
                </div>
                <Button variant="outline" size="sm" onClick={copyFromBusinessAddress}>
                  Copy Address
                </Button>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="md:col-span-2">
                  <Label htmlFor="pickup_address1">Address Line 1 *</Label>
                  <Input
                    id="pickup_address1"
                    value={pickupData.address_line1}
                    onChange={(e) => setPickupData({ ...pickupData, address_line1: e.target.value })}
                    placeholder="Building name, street address"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="pickup_address2">Address Line 2</Label>
                  <Input
                    id="pickup_address2"
                    value={pickupData.address_line2}
                    onChange={(e) => setPickupData({ ...pickupData, address_line2: e.target.value })}
                    placeholder="Landmark, area (optional)"
                  />
                </div>
                <div>
                  <Label htmlFor="pickup_city">City *</Label>
                  <Input
                    id="pickup_city"
                    value={pickupData.city}
                    onChange={(e) => setPickupData({ ...pickupData, city: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="pickup_state">State *</Label>
                  <Input
                    id="pickup_state"
                    value={pickupData.state}
                    onChange={(e) => setPickupData({ ...pickupData, state: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="pickup_pincode">Pincode *</Label>
                  <Input
                    id="pickup_pincode"
                    value={pickupData.pincode}
                    onChange={(e) => setPickupData({ ...pickupData, pincode: e.target.value })}
                    placeholder="6-digit pincode"
                    maxLength={6}
                  />
                  <p className="text-xs text-gray-500 mt-1">Shipping costs calculated from this pincode</p>
                </div>
                <div>
                  <Label htmlFor="pickup_phone">Contact Phone</Label>
                  <Input
                    id="pickup_phone"
                    value={pickupData.phone}
                    onChange={(e) => setPickupData({ ...pickupData, phone: e.target.value })}
                    placeholder="For delivery partner"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="pickup_email">Contact Email</Label>
                  <Input
                    id="pickup_email"
                    type="email"
                    value={pickupData.email}
                    onChange={(e) => setPickupData({ ...pickupData, email: e.target.value })}
                  />
                </div>
              </div>

              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <h4 className="font-medium text-amber-800 mb-2">Important Notes:</h4>
                <ul className="text-sm text-amber-700 space-y-1 list-disc list-inside">
                  <li>Ensure the pincode is correct - shipping costs depend on it</li>
                  <li>Keep the phone number updated for smooth pickup coordination</li>
                  <li>Products should be ready for pickup during business hours</li>
                </ul>
              </div>

              <Button onClick={handleSavePickupLocation} disabled={saving} className="w-full bg-green-600 hover:bg-green-700">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Save Pickup Location
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Bank & KYC Tab */}
        {activeTab === 'bank' && (
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  Bank Account Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="bank_name">Bank Name</Label>
                  <Input
                    id="bank_name"
                    value={bankData.bank_name}
                    onChange={(e) => setBankData({ ...bankData, bank_name: e.target.value })}
                    placeholder="e.g., State Bank of India"
                  />
                </div>
                <div>
                  <Label htmlFor="account_holder">Account Holder Name</Label>
                  <Input
                    id="account_holder"
                    value={bankData.account_holder}
                    onChange={(e) => setBankData({ ...bankData, account_holder: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="bank_account">Account Number</Label>
                  <Input
                    id="bank_account"
                    value={bankData.bank_account}
                    onChange={(e) => setBankData({ ...bankData, bank_account: e.target.value })}
                    placeholder="Bank account number"
                  />
                </div>
                <div>
                  <Label htmlFor="bank_ifsc">IFSC Code</Label>
                  <Input
                    id="bank_ifsc"
                    value={bankData.bank_ifsc}
                    onChange={(e) => setBankData({ ...bankData, bank_ifsc: e.target.value.toUpperCase() })}
                    placeholder="e.g., SBIN0001234"
                    maxLength={11}
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Tax Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="gst_number">GST Number</Label>
                  <Input
                    id="gst_number"
                    value={bankData.gst_number}
                    onChange={(e) => setBankData({ ...bankData, gst_number: e.target.value.toUpperCase() })}
                    placeholder="15-character GSTIN"
                    maxLength={15}
                  />
                </div>
                <div>
                  <Label htmlFor="pan_number">PAN Number</Label>
                  <Input
                    id="pan_number"
                    value={bankData.pan_number}
                    onChange={(e) => setBankData({ ...bankData, pan_number: e.target.value.toUpperCase() })}
                    placeholder="10-character PAN"
                    maxLength={10}
                  />
                </div>
                <Button onClick={handleSaveBank} disabled={saving} className="w-full bg-green-600 hover:bg-green-700 mt-4">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Bank Details
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Notifications Tab */}
        {activeTab === 'notifications' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="w-5 h-5" />
                Notification Preferences
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-500">Notification settings coming soon...</p>
            </CardContent>
          </Card>
        )}
      </div>
    </VendorLayout>
  );
}
